import { FullScreenProvider } from '@/components/FullScreen';
import { WasmLoadErrorFallback } from '@/components/WasmLoadErrorFallback';
import { WithLoader } from '@/components/WithLoader';
import { CampaignOptimisationHeader } from '@/dashboards/CampaignOptimization/components/CampaignOptimisationHeader/CampaignOptimisationHeader';
import { CampaignOptimizationSection } from '@/dashboards/CampaignOptimization/components/CampaignOptimizationSection';
import { OptimizationErrorModal } from '@/components/OptimizationErrorModal';
import { OutOfBoundsWarningModal } from '@/dashboards/CampaignOptimization/components/OutOfBoundsWarningModal';
import { CampaignActionsProvider } from '@/dashboards/CampaignOptimization/context/CampaignActionsContext';
import { OptimizationSessionProvider } from '@/dashboards/CampaignOptimization/context/OptimizationSessionContext';
import { useCampaignOptimizationUrl } from '@/dashboards/CampaignOptimization/hooks/useCampaignOptimizationUrl';
import { useGetCampaignDetails } from '@/dashboards/CampaignOptimization/hooks/useGetCampaignDetails';
import { useOptimizationSession } from '@/dashboards/CampaignOptimization/hooks/useOptimizationSession';
import { isOptimizerTimeoutError } from '@/optimizer/helpers/withOptimizerTimeout';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { FullPageLoader } from '@analytical-alley/ui';
import { useRef, useState } from 'react';

// Before the first commit there is nothing to fall back to: a failed bootstrap
// (draft still null) or a stuck worker on the first run goes to the route's
// CampaignOptimizationErrorScreen instead of the retry modal.
const isPreCommitFailure = (session: { draft: unknown; committed: unknown; error: Error | null }) =>
    !!session.error && (session.draft === null || (session.committed === null && isOptimizerTimeoutError(session.error)));

export const InitialTimeoutEscalation = () => {
    const session = useOptimizationSession();
    if (isPreCommitFailure(session)) {
        throw session.error;
    }
    return null;
};

export const RunErrorModal = () => {
    const session = useOptimizationSession();
    const { error, runOptimization, resetDraft, dismissError } = session;
    if (!error || isPreCommitFailure(session)) {
        return null;
    }
    return (
        <OptimizationErrorModal
            subject="this campaign"
            error={error}
            onRetry={runOptimization}
            onClose={() => {
                resetDraft();
                dismissError();
            }}
        />
    );
};

const RunningOverlay = ({ children }: { children: React.ReactNode }) => {
    const { isRunning } = useOptimizationSession();
    return (
        <WithLoader
            isLoading={isRunning}
            isFetching={false}
            loadingMessage="Optimizing the campaign ..."
            delayedMessage="Budget optimization calculations may take a while depending on the complexity of your data. Please be patient while we process your request."
            delayedMessageThreshold={2000}
        >
            {children}
        </WithLoader>
    );
};

export const CampaignOptimization = () => {
    const { optimizerInitError, optimizerReady } = useOptimizer();
    const { params: { campaignId } } = useCampaignOptimizationUrl();
    const { data: campaign } = useGetCampaignDetails();
    const portalRef = useRef<HTMLDivElement>(null);
    const [outOfBounds, setOutOfBounds] = useState<{ channelName?: string; min?: number; max?: number } | null>(null);

    if (optimizerInitError) {
        return <WasmLoadErrorFallback dashboardName="Campaign Optimization" error={optimizerInitError} />;
    }
    if (!optimizerReady || !campaign) {
        return <FullPageLoader />;
    }

    return (
        <OptimizationSessionProvider key={campaignId} campaign={campaign}>
            <CampaignActionsProvider campaign={campaign}>
                <InitialTimeoutEscalation />
                <CampaignOptimisationHeader />
                <RunningOverlay>
                    <div className="pb-8 px-8 lg:px-16">
                        <div className="transition-all duration-300" ref={portalRef} />
                        <FullScreenProvider portalRef={portalRef}>
                            <CampaignOptimizationSection onOutOfBoundsChannel={(info) => setOutOfBounds(info)} />
                        </FullScreenProvider>
                    </div>
                </RunningOverlay>
                {outOfBounds !== null && (
                    <OutOfBoundsWarningModal
                        onClose={() => setOutOfBounds(null)}
                        channelName={outOfBounds?.channelName}
                        minChannelRecommended={outOfBounds?.min}
                        maxChannelRecommended={outOfBounds?.max}
                    />
                )}
                <RunErrorModal />
            </CampaignActionsProvider>
        </OptimizationSessionProvider>
    );
};
